import {Injectable} from "@angular/core";
import {EmbedService} from "./embed.service";
import {PortalService} from "./portal.service";

/**
 * Names of the events that an embedded advisor sends to the embedding page.
 * - request: the request was saved in the backend (once per requestId/hash)
 * - step: the user navigated to another step
 * - result: the result page was calculated and is shown
 */
export type AdvisorEventName = 'request' | 'step' | 'result';

/**
 * Message that is sent via postMessage to the embedding page.
 *
 * Example:
 * {type: 'advisor-event', event: 'step', advisor: 'moderat', portalId: 'mona-moc', cid: null,
 *  step: 'verbrauchsanalyse/heizsystem/2', path: '/mona-moc/moderat/verbrauchsanalyse/heizsystem/2'}
 */
export interface AdvisorEventMessage {
  type: typeof AdvisorEventService.MESSAGE_TYPE;
  event: AdvisorEventName;
  advisor: string;
  portalId: string;
  cid: string | null;
  [key: string]: unknown;
}

/**
 * Informs the embedding page about the state of the advisor (request created, current step, result reached).
 * The messages are only sent if the advisor runs inside an iframe and always go to "*", they contain no data that
 * the embedding page must not know (the hash is only sent before the result if the portal allows it).
 */
@Injectable({
  providedIn: 'root'
})
export class AdvisorEventService {

  /**
   * Value of the field "type" of all event messages, so they can be told apart from the size/scroll messages.
   */
  public static readonly MESSAGE_TYPE = 'advisor-event';

  /**
   * The requestId of the last request event and if it contained a hash. The hash is encrypted with a random iv and
   * differs on every save, so a request is only sent again if it now has a hash and had none before.
   * @private
   */
  private lastRequest: {requestId: string, withHash: boolean} = null;

  /**
   * constructor
   * @param embedService
   * @param portalService
   */
  constructor(private embedService: EmbedService, private portalService: PortalService) {}

  /**
   * Sends that the request was saved in the backend. Sent once per requestId (and once more, if the hash arrives later).
   * @param requestId
   * @param hash hash to reopen the request (?hash=...), null if the portal is not allowed to get it yet
   */
  request(requestId: string, hash: string | null = null): void {
    if (!requestId) {
      return;
    }
    const withHash = !!hash;
    if (this.lastRequest?.requestId === requestId && (this.lastRequest.withHash || !withHash)) {
      return;
    }
    this.lastRequest = {requestId, withHash};
    this.send('request', {requestId, hash});
  }

  /**
   * Sends the step that is shown now.
   * @param url the router url, e.g. "/mona-moc/moderat/verbrauchsanalyse/heizsystem/2?cid=x"
   */
  step(url: string): void {
    const path = url.replace(/#.*$/, '').replace(/\?.*$/, '');
    // the step is the part after /{portalId}/{advisor}/
    const step = path.split('/').slice(3).join('/');
    this.send('step', {step, path});
  }

  /**
   * Sends that the result page was calculated.
   * @param requestId
   * @param hash hash to reopen the request (?hash=...)
   */
  result(requestId: string, hash: string | null = null): void {
    this.send('result', {requestId, hash});
  }

  /**
   * Sends an event to the embedding page, if there is one.
   * @param event
   * @param data
   */
  send(event: AdvisorEventName, data: Record<string, unknown> = {}): void {
    if (!this.embedService.isEmbedded()) {
      return;
    }

    const message: AdvisorEventMessage = {
      ...data,
      type: AdvisorEventService.MESSAGE_TYPE,
      event,
      advisor: AdvisorEventService.currentAdvisor(),
      portalId: this.portalService.currentPortalId ?? null,
      cid: this.embedService.containerId ?? null,
    };
    window.parent?.postMessage(message, "*");
  }

  /**
   * Returns the advisor from the current location (second path segment).
   * @private
   */
  private static currentAdvisor(): string {
    return window.location.pathname.split('/')[2] ?? '';
  }
}
